import { FormEvent, ReactNode, useEffect, useState } from 'react';

import './Styleguide.css';

/**
 * Internal mintkitchen styleguide.
 *
 * Hidden route (no nav/footer link): `/styleguide`
 * Password-protected via a simple session-storage gate. The password is a
 * shared team secret — it is NOT a security boundary, just a trivial
 * obstacle so the page is not indexable / casually stumbled upon.
 */

const STORAGE_KEY = 'mintkitchen-styleguide-unlocked';
const PASSWORD = 'JavaMaus';

// ---------- Tokens ---------------------------------------------------------

type ColorToken = {
  name: string;
  token: string;
  hex: string;
  rgb: string;
  role: string;
  textOn?: 'white' | 'dark';
};

const COLORS: ColorToken[] = [
  {
    name: 'Main · Teal',
    token: '--mk-primary',
    hex: '#008F99',
    rgb: '0, 143, 153',
    role: 'Primary color for everything: header, navigation, links, buttons, highlights, card accents, map markers.',
    textOn: 'white',
  },
  {
    name: 'Teal · Hover',
    token: '--mk-primary-hover',
    hex: '#006B73',
    rgb: '0, 107, 115',
    role: 'Hover and gradient endpoint toward Main · Teal.',
    textOn: 'white',
  },
  {
    name: 'Teal · Deep',
    token: '--mk-primary-deep',
    hex: '#005F66',
    rgb: '0, 95, 102',
    role: 'Active / visited link states.',
    textOn: 'white',
  },
  {
    name: 'Charcoal',
    token: '--mk-heading',
    hex: '#2C3E50',
    rgb: '44, 62, 80',
    role: 'Headings (section titles, card headlines).',
    textOn: 'white',
  },
  {
    name: 'Body',
    token: '--mk-text',
    hex: '#333333',
    rgb: '51, 51, 51',
    role: 'Primary body text.',
    textOn: 'white',
  },
  {
    name: 'Muted',
    token: '--mk-text-muted',
    hex: '#666666',
    rgb: '102, 102, 102',
    role: 'Secondary text, meta info, captions.',
    textOn: 'white',
  },
  {
    name: 'Surface',
    token: '--mk-surface',
    hex: '#F8F9FA',
    rgb: '248, 249, 250',
    role: 'Page background, soft panels, cards on white pages.',
    textOn: 'dark',
  },
  {
    name: 'Border',
    token: '--mk-border',
    hex: '#E9ECEF',
    rgb: '233, 236, 239',
    role: 'Neutral frames, dividers, card borders.',
    textOn: 'dark',
  },
  {
    name: 'Footer Dark',
    token: '--mk-footer',
    hex: '#343A40',
    rgb: '52, 58, 64',
    role: 'Footer background and dark UI areas.',
    textOn: 'white',
  },
  {
    name: 'White',
    token: '--mk-white',
    hex: '#FFFFFF',
    rgb: '255, 255, 255',
    role: 'Surfaces, text on teal buttons, cards.',
    textOn: 'dark',
  },
  {
    name: 'Black',
    token: '--mk-black',
    hex: '#000000',
    rgb: '0, 0, 0',
    role: 'Section subtitle, maximum contrast.',
    textOn: 'white',
  },
];

const FONT_WEIGHTS = [
  { label: 'Light', weight: 300, style: 'normal' as const },
  { label: 'Regular', weight: 400, style: 'normal' as const },
  { label: 'Regular Italic', weight: 400, style: 'italic' as const },
  { label: 'Bold', weight: 700, style: 'normal' as const },
  { label: 'Black', weight: 900, style: 'normal' as const },
];

const FONT_SCALE = [
  { label: 'Section title · 48px · 700', size: '48px', weight: 700, sample: 'We cook AI for small and mid-sized businesses' },
  { label: 'Hero title · 3.5rem · 700', size: '3.5rem', weight: 700, sample: 'AI Workshop · Production' },
  { label: 'H3 · 1.5rem · 600', size: '1.5rem', weight: 600, sample: 'Hands-on. Co-creative. Immediately applicable.' },
  { label: 'Section subtitle · 1.3rem · 400', size: '1.3rem', weight: 400, sample: 'By entrepreneurs, for entrepreneurs.' },
  { label: 'Body · 1rem · 400', size: '1rem', weight: 400, sample: 'Together we develop tailor-made AI solutions for your business — from the first experiment to productive deployment.' },
  { label: 'Caption · 0.875rem · 400', size: '0.875rem', weight: 400, sample: 'Ostwestfalen-Lippe · 2026' },
];

type LogoVariant = {
  title: string;
  role: string;
  src: string;
  bg: 'light' | 'dark' | 'color';
  downloads: { label: string; href: string; hint: string }[];
};

const LOGOS: LogoVariant[] = [
  {
    title: 'mintkitchen · Color',
    role: 'Standard variant for light backgrounds and official communication.',
    src: '/assets/partnerdownloads/mintKitchenLogoColor.svg',
    bg: 'color',
    downloads: [
      { label: 'SVG', href: '/assets/partnerdownloads/mintKitchenLogoColor.svg', hint: 'Web · scalable' },
      { label: 'PNG', href: '/assets/partnerdownloads/mintKitchenLogoColor.png', hint: 'Office · transparent' },
    ],
  },
  {
    title: 'mintkitchen · Black',
    role: 'Monochrome variant for non-color print and very subtle contexts.',
    src: '/assets/images/common/mintKitchenLogo.svg',
    bg: 'light',
    downloads: [
      { label: 'SVG', href: '/assets/images/common/mintKitchenLogo.svg', hint: 'Web · scalable' },
      { label: 'PNG', href: '/assets/partnerdownloads/mintKitchenLogo_black.png', hint: 'Office · transparent' },
    ],
  },
  {
    title: 'mintkitchen · White',
    role: 'For dark backgrounds (footer, video heros, keynote slides).',
    src: '/assets/partnerdownloads/mintKitchenLogo_white.png',
    bg: 'dark',
    downloads: [
      { label: 'PNG', href: '/assets/partnerdownloads/mintKitchenLogo_white.png', hint: 'Office · transparent' },
    ],
  },
];

// ---------- Gate -----------------------------------------------------------

function StyleguideGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (input === PASSWORD) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* ignore — gate is advisory, not a security boundary */
      }
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  if (unlocked) return <>{children}</>;

  return (
    <div className="styleguide-gate">
      <form className="styleguide-gate-card" onSubmit={handleSubmit} noValidate>
        <div className="styleguide-gate-logo" aria-hidden="true">
          <img src="/assets/images/common/mintKitchenLogo.svg" alt="" />
        </div>
        <h1>Internal Styleguide</h1>
        <p className="styleguide-gate-hint">
          This page is for the mintkitchen team only. Please enter the password.
        </p>
        <label className="styleguide-gate-label" htmlFor="mk-sg-password">
          Password
        </label>
        <input
          id="mk-sg-password"
          type="password"
          autoComplete="current-password"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (error) setError(false);
          }}
          className={`styleguide-gate-input${error ? ' is-error' : ''}`}
          autoFocus
          aria-invalid={error}
          aria-describedby={error ? 'mk-sg-error' : undefined}
        />
        {error && (
          <p id="mk-sg-error" className="styleguide-gate-error" role="alert">
            Password is not correct.
          </p>
        )}
        <button type="submit" className="styleguide-gate-submit">
          Unlock access
        </button>
      </form>
    </div>
  );
}

// ---------- Small building blocks -----------------------------------------

function ColorCard({ color }: { color: ColorToken }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(color.hex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked (e.g. non-https) — silently ignore */
    }
  };

  return (
    <article className="sg-color-card">
      <div
        className="sg-color-swatch"
        style={{
          backgroundColor: color.hex,
          color: color.textOn === 'dark' ? '#2C3E50' : '#ffffff',
          borderColor: color.hex === '#FFFFFF' ? '#E9ECEF' : 'transparent',
        }}
      >
        <span className="sg-color-swatch-name">{color.name}</span>
      </div>
      <div className="sg-color-body">
        <button
          type="button"
          className="sg-color-hex"
          onClick={handleCopy}
          aria-label={`Copy hex code ${color.hex}`}
          title="Click to copy"
        >
          {copied ? 'Copied!' : color.hex}
        </button>
        <dl className="sg-color-meta">
          <div>
            <dt>RGB</dt>
            <dd>{color.rgb}</dd>
          </div>
          <div>
            <dt>CSS token</dt>
            <dd><code>{color.token}</code></dd>
          </div>
        </dl>
        <p className="sg-color-role">{color.role}</p>
      </div>
    </article>
  );
}

function Placeholder({ title, children }: { title: string; children: ReactNode }) {
  return (
    <aside className="sg-placeholder" role="note">
      <span className="sg-placeholder-badge">Placeholder</span>
      <div className="sg-placeholder-body">
        <strong>{title}</strong>
        <div>{children}</div>
      </div>
    </aside>
  );
}

// ---------- Image carousel template ---------------------------------------

type CarouselSlide = {
  img: string;
  alt: string;
  heading: string;
  copy: string;
};

const SG_CAROUSEL_SLIDES: CarouselSlide[] = [
  {
    img: '/assets/images/ki-werkstatt/kiwerkstatt_hardware.jpeg',
    alt: 'Local AI hardware — workshop setup',
    heading: 'Run generative AI locally',
    copy: 'Large language models and RAG systems on everyday hardware — fully on-premise, no cloud required.',
  },
  {
    img: '/assets/images/ki-werkstatt/kiwerkstatt_software.jpeg',
    alt: 'Curated open-source AI software stack',
    heading: 'Your data stays with you',
    copy: 'Test AI ideas on real, sensitive business data. Everything stays on local machines and is deleted at the end of the session.',
  },
  {
    img: '/assets/images/ki-werkstatt/kiwerkstatt_knowhow.jpeg',
    alt: 'Hands-on guidance from the mintkitchen team',
    heading: 'Try before you buy',
    copy: 'Test your AI ideas with state-of-the-art tech before your organization commits to hardware or software.',
  },
];

function StyleguideCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  // Bumping this token resets the autoplay timer on any user interaction.
  const [resetToken, setResetToken] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % SG_CAROUSEL_SLIDES.length);
    }, 4000);
    return () => window.clearInterval(id);
  }, [resetToken]);

  const jumpTo = (index: number) => {
    setActiveIndex(index);
    setResetToken((t) => t + 1);
  };

  return (
    <div className="sg-carousel" role="region" aria-roledescription="carousel" aria-label="Example carousel">
      <div className="sg-carousel-gallery">
        <div className="sg-carousel-slides">
          {SG_CAROUSEL_SLIDES.map((slide, i) => (
            <div
              key={slide.img}
              className={`sg-carousel-slide${i === activeIndex ? ' is-active' : ''}`}
              aria-hidden={i !== activeIndex}
            >
              <img src={slide.img} alt={slide.alt} />
            </div>
          ))}
        </div>
      </div>
      <div className="sg-carousel-benefits">
        {SG_CAROUSEL_SLIDES.map((slide, i) => (
          <button
            key={slide.img}
            type="button"
            className={`sg-carousel-benefit${i === activeIndex ? ' is-active' : ''}`}
            onMouseEnter={() => jumpTo(i)}
            onFocus={() => jumpTo(i)}
            onClick={() => jumpTo(i)}
          >
            <h4 className="sg-carousel-benefit-heading">{slide.heading}</h4>
            <p className="sg-carousel-benefit-copy">{slide.copy}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- Content --------------------------------------------------------

function StyleguideContent() {
  return (
    <div className="styleguide">
      <header className="styleguide-header">
        <div className="styleguide-header-inner">
          <p className="styleguide-eyebrow">Internal document · mintkitchen</p>
          <h1>Styleguide</h1>
        </div>
      </header>

      {/* ---------- Colors ---------- */}
      <section className="sg-section" aria-labelledby="sg-colors-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-colors-heading">Colors</h2>
            <p>
              <strong>Teal</strong> is the brand's main color. It is used for all UI accents:
              header, navigation, links, buttons, highlights, card lines, icons, and map
              markers. The neutral tones (Charcoal, Body, Muted, Surface, Border, Footer) form
              the typographic and structural framework.
            </p>
          </div>
          <div className="sg-color-grid">
            {COLORS.map((c) => (
              <ColorCard key={c.token} color={c} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Logo ---------- */}
      <section className="sg-section sg-section--alt" aria-labelledby="sg-logos-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-logos-heading">Logo</h2>
            <p>
              On light backgrounds use the color or black variant; on dark backgrounds use the
              white one. The logo always gets enough clear space; no effects, no distortion, no
              recoloring in other hues.
            </p>
          </div>

          <div className="sg-logo-grid">
            {LOGOS.map((logo) => (
              <div
                key={logo.title}
                className={`sg-logo-card sg-logo-card--${logo.bg}`}
              >
                <div className="sg-logo-preview">
                  <img src={logo.src} alt={`${logo.title} logo preview`} />
                </div>
                <div className="sg-logo-meta">
                  <span className="sg-logo-title">{logo.title}</span>
                  <span className="sg-logo-role">{logo.role}</span>
                  <div className="sg-logo-downloads">
                    {logo.downloads.map((d) => (
                      <a key={d.href} href={d.href} download className="sg-logo-download">
                        <span className="sg-logo-download-format">{d.label}</span>
                        <span className="sg-logo-download-hint">{d.hint}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ---------- Typography ---------- */}
      <section className="sg-section" aria-labelledby="sg-type-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-type-heading">Typography</h2>
            <p>
              mintkitchen's brand typeface is <strong>Lato</strong> — a humanist sans by Łukasz
              Dziedzic. It is used for both body text and headlines. It is loaded globally via
              Google Fonts (<code>frontend/public/index.html</code>), with system fonts as a
              fallback.
            </p>
          </div>

          <div className="sg-font-card">
            <div className="sg-font-preview" aria-hidden="true">
              <span className="sg-font-preview-sample" style={{ fontWeight: 900 }}>Aa</span>
              <span className="sg-font-preview-sample" style={{ fontWeight: 700 }}>Aa</span>
              <span className="sg-font-preview-sample" style={{ fontWeight: 400 }}>Aa</span>
              <span
                className="sg-font-preview-sample"
                style={{ fontWeight: 400, fontStyle: 'italic' }}
              >
                Aa
              </span>
              <span className="sg-font-preview-sample" style={{ fontWeight: 300 }}>Aa</span>
            </div>
            <div className="sg-font-body">
              <span className="sg-font-title">Lato</span>
              <span className="sg-font-role">
                Humanist sans · 5 loaded weights · License: SIL Open Font License 1.1
              </span>
              <a
                href="https://fonts.google.com/specimen/Lato"
                target="_blank"
                rel="noreferrer"
                className="sg-logo-download sg-font-link"
              >
                <span className="sg-logo-download-format">Google Fonts</span>
                <span className="sg-logo-download-hint">Specimen · downloads</span>
              </a>
            </div>
          </div>

          <div className="sg-type-stack">
            <span className="sg-type-stack-label">Font stack</span>
            <code className="sg-type-stack-value">
              'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
              'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif
            </code>
          </div>

          <div className="sg-type-scale" aria-label="Typography scale">
            {FONT_SCALE.map((row) => (
              <div key={row.label} className="sg-type-row">
                <span className="sg-type-label">{row.label}</span>
                <span
                  className="sg-type-sample"
                  style={{ fontSize: row.size, fontWeight: row.weight }}
                >
                  {row.sample}
                </span>
              </div>
            ))}
          </div>

          <div className="sg-type-weights">
            <h3 className="sg-subheading">Loaded weights</h3>
            <div className="sg-type-weights-grid">
              {FONT_WEIGHTS.map((w) => (
                <div key={`${w.weight}-${w.style}`} className="sg-type-weight">
                  <span className="sg-type-weight-label">
                    {w.label} · {w.weight}
                    {w.style === 'italic' ? ' italic' : ''}
                  </span>
                  <span
                    className="sg-type-weight-sample"
                    style={{ fontWeight: w.weight, fontStyle: w.style }}
                  >
                    The quick brown fox — Ostwestfalen-Lippe 2026
                  </span>
                </div>
              ))}
            </div>
            <p className="sg-type-note">
              Additional weights (100 Hairline, 500 Medium, 600 SemiBold, 800 ExtraBold) can be
              added on demand via the <code>&lt;link&gt;</code> URL in{' '}
              <code>frontend/public/index.html</code>. The actual scale values come from{' '}
              <code>App.css</code> and <code>Pages.css</code>.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Buttons ---------- */}
      <section className="sg-section sg-section--alt" aria-labelledby="sg-buttons-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-buttons-heading">Buttons</h2>
          </div>

          <div className="sg-button-grid">
            <article className="sg-button-card">
              <div className="sg-button-preview">
                <span className="btn btn-primary">Learn more</span>
              </div>
              <div className="sg-button-label">Primary Dark</div>
            </article>

            <article className="sg-button-card">
              <div className="sg-button-preview">
                <span className="btn btn-secondary">Learn more</span>
              </div>
              <div className="sg-button-label">Secondary Dark</div>
            </article>

            <article className="sg-button-card">
              <div className="sg-button-preview sg-button-preview--on-teal">
                <span className="btn btn-primary-light">Learn more</span>
              </div>
              <div className="sg-button-label">Primary Light</div>
            </article>

            <article className="sg-button-card">
              <div className="sg-button-preview sg-button-preview--on-teal">
                <span className="btn btn-secondary-light">Learn more</span>
              </div>
              <div className="sg-button-label">Secondary Light</div>
            </article>
          </div>
        </div>
      </section>

      {/* ---------- Hero ---------- */}
      <section className="sg-section" aria-labelledby="sg-hero-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-hero-heading">Hero · Video</h2>
            <p>
              The main hero is built from a background video loop, a dark overlay, and centered
              white typography. Component:{' '}
              <code>frontend/src/components/AdvancedVideoHero.tsx</code>. Overlay default:{' '}
              <code>rgba(0, 0, 0, 0.75)</code>.
            </p>
          </div>

          <div className="sg-hero-demo" aria-label="Example hero">
            <img
              className="sg-hero-demo-image"
              src="/assets/images/ki-werkstatt/kiwerkstatt_experiment.jpeg"
              alt="Example image AI Workshop — hero demo"
            />
            <div className="sg-hero-demo-overlay" aria-hidden="true" />
            <div className="sg-hero-demo-content">
              <p className="sg-hero-demo-eyebrow">AI Workshop</p>
              <h3 className="sg-hero-demo-headline">
                We cook AI — hands-on, human, immediately applicable.
              </h3>
              <p className="sg-hero-demo-copy">
                One-day workshop for teams from production and administration.
              </p>
              <div className="sg-hero-demo-actions">
                <span className="btn btn-primary">Learn more</span>
                <span className="btn btn-secondary sg-hero-demo-secondary">Back to overview</span>
              </div>
            </div>
          </div>

          <dl className="sg-spec-list" aria-label="Hero specification">
            <div>
              <dt>Height</dt>
              <dd><code>calc(100vh - 160px)</code>, minimum height 400&nbsp;px.</dd>
            </div>
            <div>
              <dt>Overlay</dt>
              <dd>Dark, <code>rgba(0, 0, 0, 0.75)</code>. 0.7 while the video is loading.</dd>
            </div>
            <div>
              <dt>Typography</dt>
              <dd>Headline 3.5&nbsp;rem / 700, white, with a subtle <code>text-shadow</code>.</dd>
            </div>
            <div>
              <dt>Mobile fallback</dt>
              <dd>Below 768&nbsp;px: hide the video, use a teal surface (<code>#008F99</code>) as background.</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* ---------- Imagery ---------- */}
      <section className="sg-section sg-section--alt" aria-labelledby="sg-imagery-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-imagery-heading">Imagery</h2>
            <p>
              All project images are placed inside the brand wrapper — even when no text sits on
              top of them. Images without text receive a teal overlay at{' '}
              <strong>10&nbsp;%&nbsp;opacity</strong>. This makes teaser tiles, press articles,
              social posts, and blog header images feel like part of one system rather than
              arbitrary stock photos — analogous to the <strong>50&nbsp;% overlay on heros</strong>,
              only far more subtle.
            </p>
          </div>

          <div className="sg-imagery-compare">
            <figure className="sg-imagery-card">
              <div className="sg-imagery-frame">
                <img
                  className="sg-imagery-image"
                  src="/assets/images/ki-werkstatt/kiwerkstatt_experiment.jpeg"
                  alt="Raw image without overlay"
                />
                <span className="sg-imagery-badge sg-imagery-badge--warn">Do not use</span>
              </div>
              <figcaption>
                <span className="sg-imagery-label">No overlay</span>
                <span className="sg-imagery-sublabel">
                  Feels like a generic stock photo — no brand signal.
                </span>
              </figcaption>
            </figure>

            <figure className="sg-imagery-card">
              <div className="sg-imagery-frame">
                <img
                  className="sg-imagery-image"
                  src="/assets/images/ki-werkstatt/kiwerkstatt_experiment.jpeg"
                  alt="Image with mintkitchen teal overlay"
                />
                <div className="sg-imagery-overlay" aria-hidden="true" />
                <span className="sg-imagery-badge sg-imagery-badge--ok">Use like this</span>
              </div>
              <figcaption>
                <span className="sg-imagery-label sg-imagery-label--primary">
                  With mintkitchen overlay
                </span>
                <span className="sg-imagery-sublabel">
                  Teal <code>#008F99</code> · opacity <strong>10&nbsp;%</strong> — recognizable
                  as a mintkitchen image.
                </span>
              </figcaption>
            </figure>
          </div>

          <dl className="sg-spec-list">
            <div>
              <dt>Color</dt>
              <dd>
                Teal <code>#008F99</code> — the same base color as in the heros and the rest of
                the UI.
              </dd>
            </div>
            <div>
              <dt>Opacity</dt>
              <dd>
                <strong>10&nbsp;%</strong> for images without text ·{' '}
                <strong>50&nbsp;%</strong> for heros with text on top.
              </dd>
            </div>
            <div>
              <dt>Application</dt>
              <dd>
                Teaser tiles, press-article headers, social posts, blog hero images, and other
                decorative imagery on project surfaces.
              </dd>
            </div>
            <div>
              <dt>Exceptions</dt>
              <dd>
                Logos, partner marks, team portraits, graphics, and diagrams stay unchanged.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* ---------- Page layout ---------- */}
      <section className="sg-section" aria-labelledby="sg-layout-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-layout-heading">Page layout</h2>
            <p>
              Safe space between content and the viewport edges. Sections are full-bleed
              (background colour or imagery may touch the edges); every content element
              sits inside a centered container with a horizontal gutter that scales with
              the viewport. <strong>No&nbsp;text, image, tile, carousel, button, or form
              element may ever touch the browser edge</strong>, regardless of width.
            </p>
          </div>

          <figure className="sg-layout-demo">
            <figcaption className="sg-weblayout-label">
              <span>Full-bleed section · contained content</span>
              <span>Section background fills the viewport, content is inset</span>
            </figcaption>
            <div className="sg-layout-frame" aria-hidden="true">
              <div className="sg-layout-section">
                <div className="sg-layout-container">
                  <div className="sg-layout-content">
                    <span className="sg-layout-content-label">Content (max&nbsp;1200&nbsp;px)</span>
                  </div>
                </div>
                <span className="sg-layout-edge sg-layout-edge--left" aria-hidden="true" />
                <span className="sg-layout-edge sg-layout-edge--right" aria-hidden="true" />
              </div>
            </div>
            <p className="sg-weblayout-note">
              The teal band is the <code>&lt;section&gt;</code> background (reaches the
              viewport edges). The dashed inset shows the <code>.container</code> gutter —
              that&rsquo;s the safe space every content element must respect.
            </p>
            <dl className="sg-spec-list">
              <div>
                <dt>Container</dt>
                <dd>
                  <code>max-width: 1200&nbsp;px</code>, <code>margin: 0 auto</code>,{' '}
                  <code>box-sizing: border-box</code>.
                </dd>
              </div>
              <div>
                <dt>Side gutter · desktop &amp; tablet</dt>
                <dd>
                  <code>padding: 0 1.5&nbsp;rem</code> (24&nbsp;px) from{' '}
                  <strong>641&nbsp;px</strong> upward.
                </dd>
              </div>
              <div>
                <dt>Side gutter · mobile</dt>
                <dd>
                  <code>padding: 0 1&nbsp;rem</code> (16&nbsp;px) at or below{' '}
                  <strong>640&nbsp;px</strong>.
                </dd>
              </div>
              <div>
                <dt>Section background</dt>
                <dd>
                  Full viewport width — sits <em>outside</em> the container. Use the
                  section for colour, imagery, or video backgrounds that intentionally
                  bleed to the edges.
                </dd>
              </div>
              <div>
                <dt>Never</dt>
                <dd>
                  Don&rsquo;t override the container to <code>padding: 0</code> for
                  &ldquo;edge-to-edge&rdquo; hero visuals. Let the section handle the
                  bleed and keep content inside the container so it stays safe on every
                  viewport.
                </dd>
              </div>
            </dl>
          </figure>
        </div>
      </section>

      {/* ---------- Web sections ---------- */}
      <section className="sg-section sg-section--alt" aria-labelledby="sg-weblayouts-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-weblayouts-heading">Web sections</h2>
            <p>
              Reusable layout building blocks beyond the hero. All layouts share the same design
              system: teal as accent, neutral tones for structure, generous whitespace.
            </p>
          </div>

          <div className="sg-weblayouts">
            {/* Content + Visual */}
            <figure className="sg-weblayout">
              <figcaption className="sg-weblayout-label">
                <span>Content + Visual</span>
                <span>Two columns · text and image</span>
              </figcaption>
              <div className="sg-web-split">
                <div className="sg-web-split-text">
                  <p className="sg-web-eyebrow">About mintkitchen</p>
                  <h3 className="sg-web-headline">
                    We make <span className="sg-web-highlight">AI tangible</span> — for teams
                    that would otherwise never touch it.
                  </h3>
                  <p className="sg-web-copy">
                    In one-day workshops we show how AI can help concretely in the business
                    today — from quality inspection on the line to filing in the back office.
                  </p>
                  <a className="sg-web-link" href="#" onClick={(e) => e.preventDefault()}>
                    Learn more →
                  </a>
                </div>
                <figure className="sg-web-split-image">
                  <img
                    src="/assets/images/ki-werkstatt/kiwerkstatt_hardware.jpeg"
                    alt="Example image AI Workshop"
                  />
                  <span className="sg-web-split-image-overlay" aria-hidden="true" />
                </figure>
              </div>
              <p className="sg-weblayout-note">
                For body copy with a visual. Alternate the text and image sides on each
                repetition to keep the reading flow lively. Image with{' '}
                <code>border-radius: 8px</code> and the recommended teal overlay (see Imagery).
              </p>
            </figure>

            {/* Three-column feature grid */}
            <figure className="sg-weblayout">
              <figcaption className="sg-weblayout-label">
                <span>Three-column feature grid</span>
                <span>Services / benefits / KPIs</span>
              </figcaption>
              <div className="sg-web-features">
                <article className="sg-web-feature">
                  <div className="sg-web-feature-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                  </div>
                  <h4 className="sg-web-feature-title">Hands-on</h4>
                  <p className="sg-web-feature-copy">
                    Real use cases from the business — not slide theater.
                  </p>
                </article>
                <article className="sg-web-feature">
                  <div className="sg-web-feature-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21a8 8 0 0 1 16 0" />
                    </svg>
                  </div>
                  <h4 className="sg-web-feature-title">Co-creative</h4>
                  <p className="sg-web-feature-copy">
                    We build solutions together with the team, not for it.
                  </p>
                </article>
                <article className="sg-web-feature">
                  <div className="sg-web-feature-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                  </div>
                  <h4 className="sg-web-feature-title">Immediately applicable</h4>
                  <p className="sg-web-feature-copy">
                    At the end of the workshop there are concrete next steps.
                  </p>
                </article>
              </div>
              <p className="sg-weblayout-note">
                Icon in teal, headline in Charcoal, copy in Muted. Three columns from 960&nbsp;px
                upward, stacked on mobile.
              </p>
            </figure>

            {/* KPI row */}
            <figure className="sg-weblayout">
              <figcaption className="sg-weblayout-label">
                <span>KPI row</span>
                <span>Four columns · numbers with labels</span>
              </figcaption>
              <div className="sg-web-stats">
                <div className="sg-web-stat">
                  <span className="sg-web-stat-number">3</span>
                  <span className="sg-web-stat-label">Locations</span>
                </div>
                <div className="sg-web-stat">
                  <span className="sg-web-stat-number">50+</span>
                  <span className="sg-web-stat-label">Workshops delivered</span>
                </div>
                <div className="sg-web-stat">
                  <span className="sg-web-stat-number">2</span>
                  <span className="sg-web-stat-label">Variants · production &amp; administration</span>
                </div>
                <div className="sg-web-stat">
                  <span className="sg-web-stat-number">OWL</span>
                  <span className="sg-web-stat-label">Focus region</span>
                </div>
              </div>
              <p className="sg-weblayout-note">
                For KPIs, milestones, reach. Number in teal, label in Muted below. Maximum four
                values — otherwise the row feels overloaded.
              </p>
            </figure>

            {/* CTA banner */}
            <figure className="sg-weblayout">
              <figcaption className="sg-weblayout-label">
                <span>CTA banner</span>
                <span>Full-bleed teal · centered</span>
              </figcaption>
              <div className="sg-web-cta">
                <div className="sg-web-cta-content">
                  <p className="sg-web-cta-eyebrow">Join in now</p>
                  <h3 className="sg-web-cta-headline">
                    Get your team started with AI in everyday work.
                  </h3>
                  <p className="sg-web-cta-copy">
                    Free initial consultation, one day of workshop, concrete results.
                  </p>
                  <div className="sg-web-cta-actions">
                    <span className="sg-web-cta-button sg-web-cta-button--primary">Get in touch</span>
                    <span className="sg-web-cta-button sg-web-cta-button--outline">Learn more</span>
                  </div>
                </div>
              </div>
              <p className="sg-weblayout-note">
                Closing block at the end of the page. Always with one clear primary button;
                outline only as a secondary option.
              </p>
            </figure>

            {/* Image carousel · Overlay panel */}
            <figure className="sg-weblayout sg-weblayout--carousel">
              <figcaption className="sg-weblayout-label">
                <span>Image carousel · Overlay panel</span>
                <span>Auto-playing gallery paired with hoverable text tiles</span>
              </figcaption>
              <StyleguideCarousel />
              <p className="sg-weblayout-note">
                Compound layout for marquee sections. Three to five full-bleed slides fade
                through every <strong>4&nbsp;seconds</strong>; each slide is paired with a teal
                benefit tile on an overlay panel covering the left half of the frame. Hovering,
                focusing, or clicking a tile advances the gallery and resets the autoplay
                timer. Reference implementation lives on{' '}
                <code>/ki-werkstatt/verwaltung</code>.
              </p>
              <dl className="sg-spec-list">
                <div>
                  <dt>Frame</dt>
                  <dd>
                    <code>border-radius: 12&nbsp;px</code>,{' '}
                    <code>min-height: 520&ndash;650&nbsp;px</code> desktop. Slides fade with{' '}
                    <code>opacity 0.5&nbsp;s ease-in-out</code>.
                  </dd>
                </div>
                <div>
                  <dt>Overlay panel</dt>
                  <dd>
                    Absolutely positioned on the left <strong>50&nbsp;%</strong> of the frame,
                    vertically centered. Tiles sit <strong>flush to the left edge</strong> of
                    the carousel (square-left corners) and use{' '}
                    <code>background: rgba(0, 143, 153, 0.95)</code> with{' '}
                    <code>backdrop-filter: blur(10&nbsp;px)</code>.
                  </dd>
                </div>
                <div>
                  <dt>Tile states</dt>
                  <dd>
                    Idle: 4&nbsp;px Teal&nbsp;·&nbsp;Hover accent bar on the{' '}
                    <strong>right</strong> (inner) edge of the tile. Active: bar turns white and
                    the tile gradient intensifies toward Teal&nbsp;·&nbsp;Hover.
                    No&nbsp;lift effect.
                  </dd>
                </div>
                <div>
                  <dt>Timing</dt>
                  <dd>
                    Autoplay <strong>4&nbsp;s</strong> per slide. Clicking or hovering a tile
                    resets the timer. No navigation dots — slides are driven exclusively by
                    autoplay and by the paired tiles.
                  </dd>
                </div>
                <div>
                  <dt>Responsive</dt>
                  <dd>
                    Below 768&nbsp;px the overlay panel drops beneath the gallery, tiles stack
                    full-width, frame collapses to <strong>300&nbsp;px</strong> height.
                  </dd>
                </div>
              </dl>
            </figure>
          </div>
        </div>
      </section>

      {/* ---------- Open items ---------- */}
      <section className="sg-section" aria-labelledby="sg-open-heading">
        <div className="sg-section-inner">
          <div className="sg-section-head">
            <h2 id="sg-open-heading">Open items</h2>
            <p>
              Overview of all places where the styleguide is still waiting for a team decision.
            </p>
          </div>

          <ul className="sg-open-list">
            <li>
              <strong>Tone of voice:</strong> pin down the verbal styleguide (informal vs. formal
              address, sentence length, claims, no-gos) in writing — currently only implicit in
              the copy.
            </li>
            <li>
              <strong>Social media templates:</strong> add templates for LinkedIn posts,
              Instagram tiles, and stories once available.
            </li>
            <li>
              <strong>Presentation master:</strong> add PowerPoint and Keynote templates with
              title, chapter, and closing slides.
            </li>
          </ul>
        </div>
      </section>

      <footer className="styleguide-footer">
        <p>
          mintkitchen · internal styleguide — edit directly in{' '}
          <code>frontend/src/pages/Styleguide.tsx</code> &amp;{' '}
          <code>Styleguide.css</code>.
        </p>
      </footer>
    </div>
  );
}

// ---------- Page wrapper ---------------------------------------------------

export default function Styleguide() {
  return (
    <StyleguideGate>
      <StyleguideContent />
    </StyleguideGate>
  );
}
